// src/pages/EditPost.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import TuiEditor from '../components/TuiEditor';
import './NewPost.css';

function slugify(text) {
  return text
    .toString()
    .normalize('NFC')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()"'’‘”“〈〉<>\[\]\|·…\u200B]/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
}

function EditPost() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const returnToDevBlog = searchParams.get('returnTo') === 'dev-blog';
  const editorRef = useRef(null);

  const [form, setForm] = useState({
    title: '',
    summary: '',
    thumbnail: '',
    content: '',
    category: '',
    tags: '',
  });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showModal, setShowModal] = useState(null);
  const [postId, setPostId] = useState(null);
  const [postTable, setPostTable] = useState('blog_posts');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }

    const fetchPost = async () => {
      const tables = ['blog_posts', 'blog_posts_2025', 'blog_posts_2024'];
      let data = null;
      let tableName = null;
      for (const table of tables) {
        const { data: row, error } = await supabase.from(table).select('*').eq('slug', slug).single();
        if (row) {
          data = row;
          tableName = table;
          break;
        }
      }

      if (!data) {
        alert('포스트를 불러올 수 없습니다.');
        navigate(returnToDevBlog ? '/dev-blog' : '/blog');
        return;
      }

      setPostTable(tableName);
      setPostId(data.id);
      setForm({
        title: data.title,
        summary: data.summary,
        thumbnail: data.thumbnail || '',
        content: data.content,
        category: data.category || '',
        tags: Array.isArray(data.tags) ? data.tags.join(', ') : data.tags || '',
      });
      setLoading(false);
    };

    fetchPost();
  }, [slug, isAdmin, navigate, returnToDevBlog]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleThumbnailChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fileName = `thumbnail_${Date.now()}_${file.name}`;
    const { error } = await supabase.storage.from('thumbnails').upload(fileName, file);
    if (!error) {
      const { data } = supabase.storage.from('thumbnails').getPublicUrl(fileName);
      const shortUrl = data.publicUrl.split('?')[0];
      setForm((prev) => ({ ...prev, thumbnail: shortUrl }));
    } else {
      setShowModal('error');
    }
    setUploading(false);
  };

  const handleThumbnailRemove = () => {
    setForm((prev) => ({ ...prev, thumbnail: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const content = editorRef.current?.getInstance().getMarkdown();

    if (!content) {
      alert('본문 내용이 없습니다.');
      return;
    }

    const updatedPost = {
      ...form,
      content,
      tags: form.tags.split(',').map((tag) => tag.trim()),
      updated_at: new Date().toISOString(),
    };

    const { error } = await supabase
      .from(postTable)
      .update(updatedPost)
      .eq('id', postId);

    if (error) {
      console.error('Update Error:', error.message);
      setShowModal('error');
    } else {
      setShowModal('success');
      const goTo = returnToDevBlog ? '/dev-blog' : `/blog/${slug}`;
      setTimeout(() => navigate(goTo), 1000);
    }
  };

  if (loading) return <p style={{ padding: '2rem' }}>로딩 중...</p>;

  return (
    <div className="new-post">
      <h2 style={{ marginBottom: '2rem', color: '#3a3025' }}>포스트 수정</h2>
      <form onSubmit={handleSubmit} className="new-post-form">
        <input
          type="text"
          name="title"
          placeholder="제목을 입력하세요"
          value={form.title}
          onChange={handleChange}
          required
        />
        <input
          type="text"
          name="summary"
          placeholder="요약을 입력하세요"
          value={form.summary}
          onChange={handleChange}
          required
        />
        <input
          type="text"
          name="tags"
          placeholder="#태그, 쉼표로 구분"
          value={form.tags}
          onChange={handleChange}
        />
        <select name="category" value={form.category} onChange={handleChange} required>
          <option value="">카테고리 선택</option>
          <option value="tech">기술</option>
          <option value="life">일상</option>
          <option value="book">책</option>
          <option value="memoir">기억</option>
          <option value="etc">기타</option>
        </select>

        <div className="thumbnail-upload">
          <label>썸네일 이미지 수정</label>
          <input type="file" accept="image/*" onChange={handleThumbnailChange} />
          {uploading && <p>업로드 중...</p>}
          {form.thumbnail && (
            <>
              <img src={form.thumbnail} alt="썸네일 미리보기" className="thumbnail-preview" />
              <button type="button" className="remove-thumbnail" onClick={handleThumbnailRemove}>
                ✖ 썸네일 제거
              </button>
            </>
          )}
        </div>

        <TuiEditor
          ref={editorRef}
          height="500px"
          initialValue={form.content}
          initialEditType="markdown"
          previewStyle="vertical"
        />

        <div className="button-group">
          <button type="button" className="cancel-btn" onClick={() => navigate(returnToDevBlog ? '/dev-blog' : '/blog')}>
            취소하기
          </button>
          <button type="submit" className="submit-btn">
            수정완료
          </button>
        </div>
      </form>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            {showModal === 'success' && <p>✅ 글이 수정되었습니다!</p>}
            {showModal === 'error' && <p>❌ 오류가 발생했습니다. 다시 시도해 주세요.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export default EditPost;
